import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { apiCallable } from '@/lib/api'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { ClassificationChipGrid } from './ClassificationChipGrid'
import type { Classification } from '@/types'

interface AddUnknownPersonFormProps {
  groupId: string
  classifications: Classification[]
  /** Base64-encoded JPEG/PNG of the captured face (no data URL prefix). */
  faceThumbnailBase64?: string | null
  onCreated: () => void
}

/**
 * Receptionist-friendly "add new person" form. Single name input + chip-grid
 * classification picker — no Select dropdown. Uses the face thumbnail from
 * the sighting as the initial photo.
 */
export function AddUnknownPersonForm({
  groupId,
  classifications,
  faceThumbnailBase64,
  onCreated,
}: AddUnknownPersonFormProps) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [classificationId, setClassificationId] = useState<string>('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError(t('feed.drawer.nameRequired'))
      return
    }
    if (!classificationId) {
      setError(t('feed.drawer.classificationRequired'))
      return
    }
    if (!faceThumbnailBase64) {
      setError(t('feed.drawer.noPhoto'))
      return
    }

    setSaving(true)
    setError(null)
    try {
      const fn = apiCallable('createPerson')
      await fn({
        groupId,
        name: name.trim(),
        classificationId,
        notes: null,
        expiresAt: null,
        photos: [{ base64: faceThumbnailBase64 }],
      })
      onCreated()
    } catch (err: any) {
      console.error('Failed to create person:', err)
      setError(err?.message || t('feed.drawer.failedToCreate'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {faceThumbnailBase64 && (
        <div className="flex justify-center">
          <img
            src={`data:image/jpeg;base64,${faceThumbnailBase64}`}
            alt=""
            className="h-24 w-24 rounded-lg border border-rule object-cover"
          />
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="feed-add-name" className="text-[14px]">
          {t('feed.drawer.personName')}
        </Label>
        <Input
          id="feed-add-name"
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('feed.drawer.namePlaceholder')}
          className="h-11"
          disabled={saving}
        />
      </div>

      <div className="space-y-2">
        <Label className="text-[14px]">{t('feed.drawer.classification')}</Label>
        <ClassificationChipGrid
          classifications={classifications}
          selectedId={classificationId}
          onSelect={setClassificationId}
          disabled={saving}
        />
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Button
        onClick={handleSubmit}
        disabled={saving || !name.trim() || !classificationId}
        className="h-11"
      >
        {saving ? t('common.saving') : t('feed.drawer.createPerson')}
      </Button>
    </div>
  )
}
